import { ReactNode } from 'react';
import { Loader2, Wrench } from 'lucide-react';
import { useFieldProgramConfig, type FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

interface Props {
  children: (config: FieldProgramConfig | null) => ReactNode;
}

/**
 * Gates a Field Partners page behind the tenant's FieldProgramConfig.
 * - Shows a skeleton while loading.
 * - Shows an empty state if the module is not enabled (404 or enabled=false).
 * - Passes the loaded config to the render prop when active.
 */
export function FieldProgramGate({ children }: Props) {
  const { config, enabled, loading } = useFieldProgramConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  if (enabled === false) {
    return (
      <div className="mx-auto max-w-xl rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-10 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg-inset)]">
          <Wrench size={22} className="text-[var(--text-muted)]" />
        </div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">
          Field Partners module is not enabled
        </h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          This module is not enabled for your tenant. Contact your administrator
          or enable it under <span className="font-mono">Field Operations → Program Config</span>.
        </p>
      </div>
    );
  }

  return <>{children(config)}</>;
}

export default FieldProgramGate;
